import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Loader2 } from "lucide-react";

const EMPTY = { title: "", excerpt: "", content: "", cover: "", tags: "", published: true };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        tags: editing.tags ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      if (editing.id) {
        await api.put(`/admin/posts/${editing.id}`, payload);
        toast.success("Article mis à jour");
      } else {
        await api.post("/admin/posts", payload);
        toast.success("Article publié");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setEditing((ed) => ({ ...ed, cover: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    e.target.value = "";
  };

  const removePost = async (p) => {
    if (!window.confirm(`Supprimer l'article « ${p.title} » ?`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Article supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Conseils</h1>
          <p className="text-sm text-ink/60 mt-1">Écrivez vos conseils et rituels — astuce : « ## Titre » crée un sous-titre.</p>
        </div>
        <button data-testid="admin-post-new" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          <Plus size={16} /> Nouvel article
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-post-${p.slug}`} className="bg-white rounded-3xl border border-line p-4 flex items-center gap-4">
            {p.cover && <img src={p.cover} alt="" className="h-16 w-24 rounded-2xl object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-teal-dark truncate">{p.title}</p>
              <p className="text-xs text-mauve">{formatDate(p.created_at)} • {p.published ? "Publié" : "Brouillon"}</p>
            </div>
            <button data-testid={`admin-post-edit-${p.slug}`} onClick={() => setEditing({ ...EMPTY, ...p, tags: (p.tags || []).join(", ") })} className="p-2 text-teal" aria-label="Modifier"><Pencil size={16} /></button>
            <button data-testid={`admin-post-delete-${p.slug}`} onClick={() => removePost(p)} className="p-2 text-[#b04a4a]" aria-label="Supprimer"><Trash2 size={16} /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="bg-white rounded-3xl border border-line p-10 text-center text-ink/60">Aucun article — partagez votre premier conseil !</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-teal-deep/40 backdrop-blur-sm p-4" data-testid="admin-post-modal">
          <form onSubmit={save} data-lenis-prevent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain bg-white rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-teal-dark">{editing.id ? "Modifier l'article" : "Nouvel article"}</h2>
              <button type="button" data-testid="admin-post-modal-close" onClick={() => setEditing(null)} aria-label="Fermer"><X size={22} className="text-mauve" /></button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-ink">
                Titre *
                <input required data-testid="admin-post-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="block text-sm font-medium text-ink">
                Résumé
                <textarea rows={2} data-testid="admin-post-excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <div className="flex items-end gap-4">
                {editing.cover && <img src={editing.cover} alt="" className="h-20 w-28 rounded-2xl object-cover border border-line" />}
                <label data-testid="admin-post-upload" className="cursor-pointer flex items-center gap-2 rounded-2xl border border-dashed border-mauve/60 px-4 py-3 text-sm text-mauve hover:text-teal hover:border-teal transition-colors">
                  <Upload size={15} /> Image de couverture
                  <input type="file" accept="image/*" onChange={uploadCover} className="hidden" />
                </label>
              </div>
              <label className="block text-sm font-medium text-ink">
                Contenu de l'article
                <textarea
                  rows={12}
                  data-testid="admin-post-content"
                  value={editing.content}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  placeholder="Écrivez ici… Un saut de ligne vide sépare les paragraphes, une ligne commençant par ## devient un sous-titre."
                  className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal"
                />
              </label>
              <label className="block text-sm font-medium text-ink">
                Étiquettes (séparées par des virgules)
                <input data-testid="admin-post-tags" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="conseils, débuter" className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" data-testid="admin-post-published" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-teal" />
                Publier immédiatement
              </label>
            </div>
            <button type="submit" data-testid="admin-post-save" disabled={saving} className="mt-8 w-full rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
